import { h, rich, $, clear } from './dom';
import { layers } from './layers';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { sayEnglish, stopSpeech } from '../audio/speech';
import { hasMic, listenFor } from './lifeMic';
import { logShadow } from '../content/passport';
import { drawCharacter, PLAYER_LOOKS, type CharLook } from '../render/sprites';
import { NPCS } from '../world/npcs';
import { npcName } from '../i18n/labels';
import { hasGame, S } from '../core/game';

export interface Choice {
  text: string;
  /** Shown on the right, e.g. "10 min". */
  cost?: string;
  /** Disabled choices are visible but greyed out with a hint. */
  disabled?: boolean;
  /** Keep the dialog open after running (e.g. piano keys). */
  keepOpen?: boolean;
  run?: () => void;
}

export interface DialogSpec {
  who: string;
  icon?: string;
  text: string;
  choices?: Choice[];
  /** English practice line: shows 🔊 replay, 🐢 slow replay and 🎤 shadowing buttons. */
  en?: string;
}

const ID = 'dialog';
let onCloseQueue: (() => void)[] = [];
let nextExtra: Choice[] = [];

/** Extra choices appended to the next dialog only (e.g. "💬 Chat" on any NPC). */
export function setNextExtra(choices: Choice[]): void {
  nextExtra = choices;
}
export function clearNextExtra(): void {
  nextExtra = [];
}

/** Show a dialog. Choices close it (unless keepOpen) and then run their action. */
export function say(spec: DialogSpec): void {
  const box = $('dlg');
  layers.open(ID);
  box.classList.remove('show');
  void box.offsetWidth; // restart the pop-in animation
  box.classList.add('show');
  $('dlgIcon').textContent = spec.icon ?? '💬';
  $('dlgName').textContent = spec.who;
  portrait(spec.who);
  const txt = $('dlgTxt');
  clear(txt);
  txt.appendChild(rich(spec.text));
  typewriter(txt);
  enRow(box, spec.en);

  const opts = $('dlgOpts');
  clear(opts);
  const base = spec.choices?.length ? spec.choices : [{ text: m().common.ok }];
  // Extras (chat, gifts) go after the dialog's own options, so the main action stays first.
  const choices = nextExtra.length ? [...base, ...nextExtra] : base;
  nextExtra = [];
  choices.forEach((c, i) => {
    const btn = h(
      'button',
      {
        class: 'opt',
        disabled: c.disabled ?? false,
        onclick: () => {
          sfx('click');
          if (!c.keepOpen) closeDialog();
          c.run?.();
        },
      },
      h('span', { class: 'key' }, String.fromCharCode(65 + i)),
      h('span', { class: 'label' }, c.text),
      c.cost ? h('span', { class: 'cost' }, `⏱ ${c.cost}`) : null,
    );
    btn.style.animationDelay = `${0.04 * i + 0.05}s`;
    opts.appendChild(btn);
  });
  box.scrollTop = 0;
}

/** The replay / shadowing row under the dialog text (only for English practice lines). */
function enRow(box: HTMLElement, en: string | undefined): void {
  box.querySelector('#dlgEn')?.remove();
  if (!en) return;
  const t = m().life.replay;
  const status = h('span', { class: 'dlg-en-status' });
  const row = h(
    'div',
    { class: 'dlg-en', id: 'dlgEn' },
    h('button', { class: 'dlg-en-btn', 'aria-label': 'replay', onclick: () => (sfx('click'), sayEnglish(en, 0.9)) }, t.again),
    h('button', { class: 'dlg-en-btn', 'aria-label': 'slow', onclick: () => (sfx('click'), sayEnglish(en, 0.55)) }, t.slow),
    hasMic()
      ? h(
          'button',
          {
            class: 'dlg-en-btn mic',
            'aria-label': 'shadow',
            onclick: (e: MouseEvent) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.classList.add('on');
              status.className = 'dlg-en-status';
              status.textContent = t.listening;
              const ok = listenFor(
                en,
                (good, heard) => {
                  btn.classList.remove('on');
                  status.className = `dlg-en-status ${good ? 'ok' : 'bad'}`;
                  status.textContent = good ? `✅ ${t.good}` : t.retry(heard || '…');
                  if (good) {
                    logShadow(en.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim().slice(0, 60));
                    sfx('ok');
                  } else sfx('bad');
                },
                () => {
                  btn.classList.remove('on');
                  status.textContent = m().english.noMic;
                },
              );
              if (!ok) status.textContent = m().english.noMic;
            },
          },
          t.mic,
        )
      : null,
    status,
  );
  const opts = $('dlgOpts');
  opts.parentElement!.insertBefore(row, opts);
}

// --- Portraits: the speaker's own sprite, drawn big, when the name matches someone in the cast. ---
const portraits = new Map<string, HTMLCanvasElement>();
function lookFor(who: string): CharLook | null {
  const t = m().common;
  if (hasGame() && (who === t.me || who === t.thoughts)) return PLAYER_LOOKS[S().player.look] ?? PLAYER_LOOKS[0];
  // Names may carry a role suffix ("Uncle Chun · Janitor").
  const base = who.split(' · ')[0].trim();
  const npc = NPCS.find((n) => n.look && npcName(n.label) === base);
  return npc?.look ?? null;
}
function portrait(who: string): void {
  const box = $('dlgPortrait');
  box.querySelector('canvas')?.remove();
  const look = lookFor(who);
  box.classList.toggle('has-canvas', !!look);
  if (!look) return;
  const key = JSON.stringify(look);
  let cv = portraits.get(key);
  if (!cv) {
    cv = document.createElement('canvas');
    cv.width = 128;
    cv.height = 152;
    const c = cv.getContext('2d')!;
    c.imageSmoothingEnabled = false;
    c.scale(5, 5);
    drawCharacter(c, look, 12.8, 30, { facing: 'down', phase: 0, moving: false, blink: false, breath: 0 });
    portraits.set(key, cv);
  }
  box.appendChild(cv);
}

export function closeDialog(): void {
  if (!layers.isOpen(ID)) return;
  $('dlg').classList.remove('show');
  layers.close(ID);
  stopSpeech();
  const q = onCloseQueue;
  onCloseQueue = [];
  q.forEach((f) => f());
}

export function dialogOpen(): boolean {
  return layers.isOpen(ID);
}

/** Keyboard: 1-9 / A-I pick a choice, Esc closes. Returns true if handled. */
export function dialogKey(e: KeyboardEvent): boolean {
  if (!dialogOpen()) return false;
  if (e.key === 'Escape') {
    closeDialog();
    return true;
  }
  const buttons = Array.from($('dlgOpts').querySelectorAll<HTMLButtonElement>('button.opt'));
  let idx = -1;
  if (/^[1-9]$/.test(e.key)) idx = Number(e.key) - 1;
  else if (/^[a-i]$/i.test(e.key) && !e.repeat) idx = e.key.toLowerCase().charCodeAt(0) - 97;
  else if ((e.key === 'Enter' || e.key === ' ') && buttons.length === 1) idx = 0;
  if (idx >= 0 && buttons[idx] && !buttons[idx].disabled) {
    buttons[idx].click();
    return true;
  }
  return e.key !== 'Tab';
}

/** Reveal text progressively; a tap on the text completes it. */
function typewriter(el: HTMLElement): void {
  const full = Array.from(el.childNodes);
  const texts: { node: Text; full: string }[] = [];
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) texts.push({ node: n as Text, full: n.textContent ?? '' });
    else n.childNodes.forEach(walk);
  };
  full.forEach(walk);
  texts.forEach((t) => (t.node.textContent = ''));
  let ti = 0, ci = 0;
  const total = texts.reduce((a, t) => a + t.full.length, 0);
  const perTick = Math.max(1, Math.ceil(total / 45));
  const tick = () => {
    if (!el.isConnected) return;
    for (let k = 0; k < perTick && ti < texts.length; k++) {
      const t = texts[ti];
      ci++;
      t.node.textContent = t.full.slice(0, ci);
      if (ci >= t.full.length) {
        ti++;
        ci = 0;
      }
    }
    if (ti < texts.length) timer = window.setTimeout(tick, 16);
  };
  let timer = window.setTimeout(tick, 16);
  el.onclick = () => {
    window.clearTimeout(timer);
    texts.forEach((t) => (t.node.textContent = t.full));
    ti = texts.length;
  };
}
